import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CalendarDays,
  GraduationCap,
  Image as ImageIcon,
  Lock,
  MapPin,
  Save,
  School,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { SecureImage } from '@/components/ui/SecureImage';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { boardLabel, INSTITUTION_TYPE_LABEL } from '@/constants/institute';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import {
  AcademicStep,
  ContactStep,
  IdentityStep,
  PeopleStep,
} from '@/features/platform/institutes/components/InstituteWizardSteps';
import {
  instituteSchema,
  withClearedFields,
  type InstituteFormValues,
} from '@/features/platform/institutes/schemas/institute.schema';
import { CONSOLE_PALETTE, CONSOLE_PANEL_SKIN } from '../../theme';
import { instituteApi } from '../../api/institute.api';
import { useInstituteBranding } from '../../store/branding.store';
import { QueryError } from '@/components/ui/QueryError';
import { applyFieldErrors, toMobileDigits } from '@/lib/forms';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

type TabKey = 'identity' | 'academic' | 'contact' | 'people' | 'branding';

interface ProfileTab {
  key: TabKey;
  label: string;
  icon: LucideIcon;
  description: string;
  /**
   * The fields this section owns.
   *
   * A tab that is not on screen still hides its own errors, so the rail marks
   * the ones that need attention — otherwise a failed save on the identity tab
   * says nothing at all to someone standing on the address tab.
   */
  fields: readonly (keyof InstituteFormValues)[];
}

const TABS: readonly ProfileTab[] = [
  {
    key: 'identity',
    label: 'Identity & Recognition',
    icon: School,
    description: 'Basic identity details of your institute as per official records.',
    fields: [
      'institutionType',
      'code',
      'subdomain',
      'legalName',
      'displayName',
      'shortName',
      'motto',
      'udiseCode',
      'board',
      'stateBoardName',
      'affiliationNumber',
      'affiliationValidTill',
      'recognitionNumber',
      'establishedYear',
      'managementType',
      'trustName',
      'trustRegistrationNo',
      'panNumber',
      'gstin',
      'aadhaarNumber',
    ],
  },
  {
    key: 'academic',
    label: 'Academic Profile',
    icon: GraduationCap,
    description: 'Affiliation, class ranges, level, and educational structure.',
    fields: [
      'schoolLevel',
      'lowestClass',
      'highestClass',
      'genderType',
      'residentialType',
      'mediumOfInstruction',
      'streams',
    ],
  },
  {
    key: 'contact',
    label: 'Address & Contact',
    icon: MapPin,
    description: 'Physical address, official phone numbers, email, and location details.',
    fields: [
      'addressLine1',
      'addressLine2',
      'locality',
      'state',
      'district',
      'city',
      'pincode',
      'phone',
      'alternatePhone',
      'whatsappNumber',
      'email',
      'website',
    ],
  },
  {
    key: 'people',
    label: 'People',
    icon: Users,
    description: 'Principal and administrative contact details.',
    fields: [
      'principalName',
      'principalPhone',
      'principalEmail',
      'adminContactName',
      'adminContactDesignation',
      'adminContactPhone',
      'adminContactEmail',
      'subscriptionAmount',
      'paymentStatus',
    ],
  },
  {
    key: 'branding',
    label: 'Logo & Branding',
    icon: ImageIcon,
    description: 'Official institute logo for report cards and receipts.',
    fields: [],
  },
];

/** A fact about the institute, drawn over the banner's wash. */
function HeroChip({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-3 py-1 text-[11px] font-bold text-slate-700 dark:text-slate-200 backdrop-blur-sm">
      <Icon className="h-3.5 w-3.5 shrink-0 text-blue-500 dark:text-blue-400" aria-hidden />
      {children}
    </span>
  );
}

/**
 * The campus drawn beside the institute's name.
 *
 * Decorative and `aria-hidden` — it says nothing the heading beside it does not.
 * Built from `fill-*` utilities rather than gradient stops so it re-tints with
 * the theme, and animated only with the keyframes the product already ships
 * (`styles/index.css`), every one of which is held still under
 * `prefers-reduced-motion`.
 */
function CampusGraphic() {
  return (
    <svg viewBox="0 0 200 140" className="h-auto w-full" role="presentation" aria-hidden>
      {/* the sky behind it */}
      <circle cx="100" cy="58" r="50" className="fill-blue-200/60 dark:fill-blue-500/10" />
      <circle
        cx="100"
        cy="58"
        r="34"
        className="fill-blue-200/60 animate-glow dark:fill-blue-500/10"
      />

      {/* the ground the campus stands on */}
      <rect x="6" y="118" width="188" height="7" rx="3.5" className="fill-blue-300/60 dark:fill-blue-400/25" />

      {/* the two side blocks, lower than the hall between them */}
      <rect x="20" y="76" width="42" height="42" rx="7" className="fill-blue-400 dark:fill-blue-500" />
      <rect x="138" y="76" width="42" height="42" rx="7" className="fill-sky-400 dark:fill-sky-500" />

      {/* the main hall */}
      <rect x="62" y="54" width="76" height="64" rx="9" className="fill-blue-500 dark:fill-blue-400" />
      <path d="M100 20 L150 56 L50 56 Z" className="fill-blue-600 dark:fill-blue-500" />
      <rect x="56" y="52" width="88" height="6" rx="3" className="fill-blue-700 dark:fill-blue-600" />

      {/* the flag over the pediment, breathing in and out of the wind */}
      <g className="animate-float-slow">
        <rect x="99" y="4" width="2" height="18" rx="1" className="fill-blue-700 dark:fill-blue-300" />
        <path d="M101 6 L118 10 L101 15 Z" className="fill-amber" />
      </g>

      {/* lit windows, breathing out of step with each other */}
      <rect x="72" y="68" width="12" height="12" rx="3" className="fill-white/85 animate-glow dark:fill-slate-100/70" />
      <rect x="116" y="68" width="12" height="12" rx="3" className="fill-white/85 animate-glow delay-300 dark:fill-slate-100/70" />
      <rect x="30" y="88" width="10" height="10" rx="3" className="fill-white/80 animate-glow delay-500 dark:fill-slate-100/60" />
      <rect x="44" y="88" width="10" height="10" rx="3" className="fill-white/80 animate-glow delay-100 dark:fill-slate-100/60" />
      <rect x="148" y="88" width="10" height="10" rx="3" className="fill-white/80 animate-glow delay-700 dark:fill-slate-100/60" />
      <rect x="162" y="88" width="10" height="10" rx="3" className="fill-white/80 animate-glow delay-200 dark:fill-slate-100/60" />

      {/* the clock every school building has, and the door under it */}
      <circle cx="100" cy="40" r="8" className="fill-amber" />
      <path d="M100 36 L100 40 L103 42" className="stroke-slate-800 fill-none" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M90 118 L90 96 A10 10 0 0 1 110 96 L110 118 Z" className="fill-white/90 dark:fill-slate-900/70" />

      {/* the steps up to it */}
      <rect x="82" y="114" width="36" height="4" rx="2" className="fill-blue-300 dark:fill-blue-600" />
      <rect x="76" y="118" width="48" height="4" rx="2" className="fill-blue-300 dark:fill-blue-600" />

      {/* sparks of light around the roof */}
      <circle cx="34" cy="46" r="3" className="fill-amber animate-sparkle" />
      <circle cx="168" cy="38" r="2.5" className="fill-blue-400 animate-sparkle delay-500" />
      <circle cx="154" cy="60" r="2" className="fill-sky-400 animate-sparkle delay-200" />
      <circle cx="46" cy="66" r="2" className="fill-blue-400 animate-sparkle delay-700" />
    </svg>
  );
}

/**
 * An institute editing its own record.
 *
 * The same form the platform console uses, down to the step components and the
 * schema (CLAUDE.md §3) — an institute correcting its address should be answering
 * the same questions our own staff would, not a second set that drifts.
 *
 * Laid out as a rail beside the fields rather than a strip of tabs above them:
 * five sections of a record that is read as a whole, so the reader can see every
 * section — and which of them still has something wrong — while typing into one.
 *
 * `code` is shown but locked: the system assigns it, and it is the reference
 * our staff and the school's receipts know the institute by.
 */
export function InstituteProfilePage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canEdit = Boolean(permissions?.includes('INSTITUTE_PROFILE_EDIT'));
  const loadBranding = useInstituteBranding((state) => state.load);
  const logoVersion = useInstituteBranding((state) => state.version);

  const [tab, setTab] = useState<TabKey>('identity');
  const [formError, setFormError] = useState<string | null>(null);

  const {
    data: institute,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => instituteApi.getProfile(), []);

  const form = useForm<InstituteFormValues>({
    resolver: zodResolver(instituteSchema),
    mode: 'onBlur',
  });
  const {
    register,
    control,
    watch,
    reset,
    getValues,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = form;
  useLiveFormErrors(form);

  useEffect(() => {
    if (!institute) return;

    reset({
      code: institute.code,
      subdomain: institute.subdomain ?? '',
      // The kind of institute is the record's own. Left out, the form took the
      // select's first option (SCHOOL), showed school-only fields and would have
      // saved a college as a CBSE school.
      institutionType: institute.institutionType,
      collegeLevel: institute.collegeLevel ?? undefined,
      legalName: institute.legalName,
      displayName: institute.displayName,
      shortName: institute.shortName ?? '',
      motto: institute.motto ?? '',
      udiseCode: institute.udiseCode ?? '',
      panNumber: institute.panNumber ?? '',
      gstin: institute.gstin ?? '',
      board: institute.board ?? undefined,
      stateBoardName: institute.stateBoardName ?? '',
      affiliationNumber: institute.affiliationNumber ?? '',
      affiliationValidTill: institute.affiliationValidTill?.slice(0, 10) ?? '',
      recognitionNumber: institute.recognitionNumber ?? '',
      establishedYear: institute.establishedYear ?? '',
      managementType: institute.managementType,
      trustName: institute.trustName ?? '',
      trustRegistrationNo: institute.trustRegistrationNo ?? '',

      schoolLevel: institute.schoolLevel ?? undefined,
      lowestClass: institute.lowestClass as InstituteFormValues['lowestClass'],
      highestClass: institute.highestClass as InstituteFormValues['highestClass'],
      genderType: institute.genderType,
      residentialType: institute.residentialType,
      mediumOfInstruction: institute.mediumOfInstruction,
      streams: institute.streams ?? [],

      addressLine1: institute.addressLine1,
      addressLine2: institute.addressLine2 ?? '',
      locality: institute.locality ?? '',
      city: institute.city,
      district: institute.district,
      state: institute.state as InstituteFormValues['state'],
      pincode: institute.pincode,
      // Loaded so a save sends the pin back as it is — left out, the blank
      // fields went up as null and every save wiped the campus map pin.
      latitude: institute.latitude ?? '',
      longitude: institute.longitude ?? '',
      // Saved as +91XXXXXXXXXX; the phone fields draw the +91 and hold ten digits.
      phone: toMobileDigits(institute.phone ?? ''),
      alternatePhone: toMobileDigits(institute.alternatePhone ?? ''),
      whatsappNumber: toMobileDigits(institute.whatsappNumber ?? ''),
      email: institute.email,
      website: institute.website ?? '',

      principalName: institute.principalName,
      principalPhone: toMobileDigits(institute.principalPhone ?? ''),
      principalEmail: institute.principalEmail ?? '',
      adminContactName: institute.adminContactName,
      adminContactDesignation: institute.adminContactDesignation ?? '',
      adminContactPhone: toMobileDigits(institute.adminContactPhone ?? ''),
      adminContactEmail: institute.adminContactEmail,
      // Not shown or sent from this screen (the submit strips them), but the
      // shared schema requires them — without them validation failed silently
      // and Save did nothing.
      subscriptionAmount:
        institute.subscriptionAmount === null ? '' : Number(institute.subscriptionAmount),
      paymentStatus: institute.paymentStatus,
      subscriptionDueDate: institute.subscriptionDueDate?.slice(0, 10) ?? '',
    });
  }, [institute, reset]);

  const onSubmit = handleSubmit(async () => {
    setFormError(null);
    try {
      // `code` is immutable and the server rejects it here — strip it rather
      // than relying on the server to ignore it.
      // Parsed from the raw form values, not the ones `handleSubmit` passes in:
      // those have already been through the resolver's transforms, and a second
      // pass rejects them (`subscriptionDueDate` is `null` by then), which
      // failed every save with a generic error.
      // The licence fields go too: they are the platform's record of what this
      // school pays us, not the school's to send, and the server ignores them.
      const {
        code: _code,
        subscriptionAmount: _fee,
        paymentStatus: _paid,
        subscriptionDueDate: _due,
        ...profile
      } = instituteSchema.parse(getValues());

      // An emptied optional field goes up as null, so clearing a website or
      // motto here really clears it rather than keeping the old value.
      const updated = await instituteApi.updateProfile(withClearedFields(profile));
      // A toast, like every other save in the product — the banner this used to
      // raise was the one place a confirmation was drawn on the page itself.
      toast.success('Changes saved', `${updated.displayName} has been updated.`);
      refetch();
      // The sidebar shows the institute's name — reload it so a rename is visible
      // straight away rather than after the next sign-in.
      void loadBranding();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  if (error) {
    return (
      <div className={CONSOLE_PALETTE}>
        <PageHeader title="Institute profile" />
        <QueryError title="Could not load your institute" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (isLoading || !institute) {
    return (
      <div className={CONSOLE_PALETTE}>
        <PageHeader title="Loading your institute…" />
        <div className="rounded-3xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-5 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
          <SkeletonLines count={6} />
        </div>
      </div>
    );
  }

  // `isEdit` locks the institute code in the shared step component.
  const stepProps = { register, errors, control, watch, setValue, isEdit: true };

  const isCollege = institute.institutionType === 'COLLEGE';

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE)}>
      {/* Banner: the record's own cover — crest, name, and the facts that
          identify it, over an indigo wash shared with the rest of the console. */}
      <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-[#172554]/50 p-5 sm:p-7 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
        {/* Two lights drifting out of step behind the wash, and a single pass of
            light across it. All decoration, all held still under
            prefers-reduced-motion. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/30 to-blue-500/20 blur-3xl animate-orb"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl animate-orb-slow"
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen dark:via-white/10" />
        </div>

        <div className="relative flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col sm:flex-row items-start gap-3.5 sm:gap-4">
            <div className="grid h-14 w-14 sm:h-16 sm:w-16 shrink-0 place-items-center overflow-hidden rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25">
              {institute.logoKey ? (
                <SecureImage
                  path={instituteApi.logoPath()}
                  alt=""
                  reloadKey={logoVersion}
                  className="h-full w-full bg-white object-contain p-1.5"
                  fallback={<School className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.9} />}
                />
              ) : (
                <School className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.9} aria-hidden />
              )}
            </div>

            <div className="min-w-0">
              <span className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                Your institute
              </span>
              <h1 className="mt-0.5 text-lg sm:text-xl font-black tracking-tight text-slate-900 dark:text-white lg:text-2xl">
                {institute.displayName}
              </h1>
              <p className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300 sm:text-sm">
                Keep these details right — they appear on receipts, report cards and certificates.
              </p>

              <div className="mt-2.5 sm:mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
                <HeroChip icon={GraduationCap}>
                  {INSTITUTION_TYPE_LABEL[institute.institutionType]}
                </HeroChip>
                {!isCollege && institute.board && (
                  <HeroChip icon={ShieldCheck}>{boardLabel(institute.board)}</HeroChip>
                )}
                <HeroChip icon={MapPin}>
                  {institute.city}, {institute.state}
                </HeroChip>
                {institute.establishedYear && (
                  <HeroChip icon={CalendarDays}>Est. {institute.establishedYear}</HeroChip>
                )}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-4 sm:gap-5 w-full lg:w-auto">
            <div className="hidden w-[180px] shrink-0 lg:block xl:w-[210px]">
              <CampusGraphic />
            </div>

            {/* The one field on this page nobody may change, said plainly rather
                than left as a greyed-out box the reader has to go and find. */}
            <div className="w-full lg:w-auto shrink-0 rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-white/70 dark:bg-slate-900/50 px-4 py-2.5 sm:px-5 sm:py-3 flex lg:flex-col items-center justify-between lg:justify-center text-center backdrop-blur-sm">
              <div className="flex lg:flex-col items-center gap-2 lg:gap-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">
                  Institute code
                </p>
                <p className="font-mono text-sm sm:text-base font-black tracking-widest text-slate-800 dark:text-white">
                  {institute.code}
                </p>
              </div>
              <p className="lg:mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <Lock className="h-3 w-3" aria-hidden />
                Locked
              </p>
            </div>
          </div>
        </div>
      </div>

      {!canEdit && (
        <Alert tone="info" title="Read only">
          Your role lets you see the institute profile but not change it. Ask your institute
          administrator to make the correction.
        </Alert>
      )}

      {formError && (
        <Alert tone="critical" title="Could not save">
          {formError}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 animate-slide-down delay-100">
        {/* The rail. A scrolling strip of chips on a phone, a standing list on a
            desktop — one set of buttons either way, so the two cannot drift. */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="lg:sticky lg:top-6 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-3 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
            <p className="hidden px-2 pb-2 pt-1 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 lg:block">
              Profile sections
            </p>

            <div
              role="tablist"
              aria-label="Institute profile sections"
              className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              {TABS.map((item, index) => {
                const Icon = item.icon;
                const isActive = tab === item.key;
                const hasErrors = item.fields.some((field) => errors[field]);

                return (
                  <button
                    key={item.key}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    onClick={(e) => {
                      setTab(item.key);
                      e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                    className={cn(
                      'group flex shrink-0 cursor-pointer items-center lg:items-start gap-2.5 sm:gap-3 rounded-xl sm:rounded-2xl border p-2.5 sm:p-3 text-left transition-colors duration-150 whitespace-nowrap lg:whitespace-normal lg:w-full',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50',
                      isActive
                        ? 'border-blue-200 dark:border-blue-800/70 bg-blue-50/70 dark:bg-blue-950/40 shadow-xs'
                        : 'border-transparent hover:border-slate-200 dark:hover:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50',
                    )}
                  >
                    <span className="relative shrink-0" aria-hidden>
                      <span
                        className={cn(
                          'relative grid h-7 w-7 sm:h-9 sm:w-9 place-items-center overflow-hidden rounded-lg sm:rounded-xl border',
                          isActive
                            ? 'border-blue-400/30 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-blue-500/30'
                            : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200 transition-colors',
                        )}
                      >
                        <Icon className="relative h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </span>
                      {hasErrors && (
                        <span className="absolute -right-1 -top-1 h-2 sm:h-2.5 w-2 sm:w-2.5 rounded-full border-2 border-white dark:border-[#111c2e] bg-rose-500" />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-[13px] sm:text-[14px] font-extrabold whitespace-nowrap',
                            isActive
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-700 dark:text-slate-300',
                          )}
                        >
                          {item.label}
                        </span>
                        <span className="ml-auto shrink-0 font-mono text-[11px] sm:text-[12px] font-bold text-slate-400 dark:text-slate-600">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </span>
                      <span className="mt-1 hidden text-[11px] font-medium leading-snug text-slate-500 dark:text-slate-400 lg:block">
                        {item.description}
                      </span>
                      {hasErrors && (
                        <span className="mt-1 hidden text-[11px] font-bold text-rose-600 dark:text-rose-400 lg:block">
                          Needs attention
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* The fields for whichever section the rail is standing on. */}
        <section className="flex flex-col gap-5 lg:col-span-8 xl:col-span-9">
          
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
            <fieldset disabled={!canEdit || isSubmitting} className="contents">
              {/* The shared `FormSection` panels are given this page's softer
                  corners here rather than in the component, so the platform
                  console's own screens are left as they are. Their colours come
                  from `CONSOLE_PALETTE` on the root, not from overrides here. */}
              <div className={cn('flex flex-col gap-5 pb-12', CONSOLE_PANEL_SKIN)}>
                <div
                  key={`tab-${tab}-identity`}
                  className={tab === 'identity' ? 'block animate-tab-fade' : 'hidden'}
                >
                  <IdentityStep {...stepProps} />
                </div>
                <div
                  key={`tab-${tab}-academic`}
                  className={tab === 'academic' ? 'block animate-tab-fade' : 'hidden'}
                >
                  <AcademicStep {...stepProps} />
                </div>
                <div
                  key={`tab-${tab}-contact`}
                  className={tab === 'contact' ? 'block animate-tab-fade' : 'hidden'}
                >
                  <ContactStep {...stepProps} />
                </div>
                <div
                  key={`tab-${tab}-people`}
                  className={tab === 'people' ? 'block animate-tab-fade' : 'hidden'}
                >
                  <PeopleStep {...stepProps} showSubscription={false} />
                </div>
                <div
                  key={`tab-${tab}-branding`}
                  className={tab === 'branding' ? 'block animate-tab-fade' : 'hidden'}
                >
                  <BrandingPanel canEdit={canEdit} hasLogo={Boolean(institute.logoKey)} />
                </div>
              </div>
            </fieldset>

            {/* The save bar rides the bottom of the viewport */}
            {canEdit && tab !== 'branding' && (
              <div className="sticky bottom-4 z-20 w-full rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white/95 dark:bg-[#111c2e]/95 px-4 py-3 sm:px-6 sm:py-3.5 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.45)] backdrop-blur-xl">
                <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4">
                  <p className="flex min-w-0 items-center gap-2.5 text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200">
                    <span
                      className={cn(
                        'h-2.5 w-2.5 shrink-0 rounded-full',
                        isDirty ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500',
                      )}
                      aria-hidden
                    />
                    {isDirty
                      ? 'You have unsaved changes on this page.'
                      : 'Everything on this page is saved.'}
                  </p>

                  <button
                    type="submit"
                    disabled={!isDirty || isSubmitting}
                    className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm shadow-indigo-500/20 active:scale-[0.98] inline-flex cursor-pointer items-center gap-2 transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 w-full sm:w-auto justify-center"
                  >
                    {isSubmitting ? (
                      <span>Saving…</span>
                    ) : (
                      <>
                        <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                        <span>Save changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </section>
      </div>
    </div>
  );
}

/**
 * The institute's logo.
 *
 * Uploads and deletes take effect immediately rather than waiting for the form
 * below to be saved — a file is not a form field, and asking someone to press
 * "Save changes" after a picture has visibly changed is a good way to have them
 * lose it. The sidebar is reloaded on both, so the new mark appears at once.
 */
function BrandingPanel({ canEdit, hasLogo }: { canEdit: boolean; hasLogo: boolean }) {
  const loadBranding = useInstituteBranding((state) => state.load);
  const logoVersion = useInstituteBranding((state) => state.version);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleUpload(file: File) {
    setIsUploading(true);
    setError(null);
    try {
      await instituteApi.uploadLogo(file);
      await loadBranding();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'That image could not be uploaded.',
      );
    } finally {
      setIsUploading(false);
    }
  }

  async function handleRemove() {
    setError(null);
    try {
      await instituteApi.removeLogo();
      await loadBranding();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'The logo could not be removed.',
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert tone="critical" title="Could not update your logo">
          {error}
        </Alert>
      )}

      <div className="rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] sm:p-5">
        <div className="max-w-md">
          {canEdit ? (
            <ImageUpload
              label="Institute logo"
              isUploading={isUploading}
              onSelect={handleUpload}
              onRemove={hasLogo ? handleRemove : undefined}
              hint="Appears in your sidebar and on printed report cards. A square PNG with a transparent background works best."
              current={
                hasLogo ? (
                  <SecureImage
                    path={instituteApi.logoPath()}
                    alt="Your institute logo"
                    reloadKey={logoVersion}
                    className="h-full w-full p-1"
                  />
                ) : undefined
              }
            />
          ) : (
            // No drag-and-drop frame for someone who cannot use it — just the mark.
            <div className="flex flex-col gap-2">
              <p className="lbl">Institute logo</p>
              <div className="grid h-32 w-32 place-items-center overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                {hasLogo ? (
                  <SecureImage
                    path={instituteApi.logoPath()}
                    alt="Your institute logo"
                    reloadKey={logoVersion}
                    className="h-full w-full p-2"
                  />
                ) : (
                  <span className="text-sm font-medium text-slate-400">None yet</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
